// packages import
import { Autocomplete, TextField } from '@mui/material'
import { FC, useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

// others
import {
  ExternalUserRoleRole,
  useFetchAllExternalUsersLazyQuery,
  ExternalUser,
  Maybe,
  OrderExternalUsersBy
} from '../../../generated'
import { PAGE_LIMIT } from '../../constants'
import { ExternalUsersResult } from '../../interfaceTypes/common'

interface ClientDropdownProps {
  selectedClientId?: string
  onChange: (value: Maybe<ExternalUser>) => void
  label?: string
  placeholder?: string
  errorMessage?: string
  disabled?: boolean
  sx?: object
  autoSelectOnTab?: boolean
  size?: 'small' | 'medium'
}

const ClientDropdown: FC<ClientDropdownProps> = ({
  selectedClientId,
  onChange,
  label = 'Client',
  placeholder,
  errorMessage,
  disabled = false,
  size = 'medium',
  autoSelectOnTab = true,
  sx = { minWidth: 150 }
}) => {
  const [clients, setClients] = useState<ExternalUsersResult>([])
  const [clientSearchTerm, setClientSearchTerm] = useState<string>('')

  const [clientPage, setClientPage] = useState<number>(1)
  const [hasMorePages, setHasMorePages] = useState<boolean>(true)

  const value = clients?.find(client => client?.id === selectedClientId) || null

  const [debouncedClientSearchTerm] = useDebounce(clientSearchTerm, 500)

  // Reset pagination when search term changes (clients replaced by onCompleted when page===1)
  useEffect(() => {
    setClientPage(1)
    setHasMorePages(true)
  }, [debouncedClientSearchTerm])

  const [fetchClients, { loading: clientLoading }] = useFetchAllExternalUsersLazyQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: undefined,
    onError() {},

    onCompleted(data) {
      const { fetchAllExternalUsers } = data || {}
      const { externalUsers, pagination } = fetchAllExternalUsers || {}
      if (externalUsers && externalUsers?.length > 0) {
        const { page, totalPages } = pagination || {}
        setClients(prevClients => {
          return page === 1 ? externalUsers : [...(prevClients || []), ...externalUsers]
        })
        setHasMorePages((page || 0) < (totalPages || 0))
      } else setHasMorePages(false)
    }
  })

  const handleClientChange = (newValue: ExternalUser) => {
    onChange(newValue)
  }

  useEffect(() => {
    hasMorePages &&
      fetchClients({
        variables: {
          externalUsersInput: {
            paginationOptions: { page: clientPage, limit: PAGE_LIMIT },
            roles: [ExternalUserRoleRole.Client],
            searchValue: debouncedClientSearchTerm || undefined,
            orderBy: OrderExternalUsersBy.FirstName,
            orderByDirection: 'ASC'
          }
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPage, debouncedClientSearchTerm, hasMorePages])

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listboxNode = event.currentTarget
      if (
        listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
        hasMorePages &&
        !clientLoading
      ) {
        setClientPage(prev => prev + 1)
      }
    },
    [hasMorePages, clientLoading]
  )

  return (
    <Autocomplete
      sx={sx}
      options={clients ?? []}
      value={value}
      onChange={(_, newValue) => handleClientChange(newValue as ExternalUser)}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') setClientSearchTerm(newInputValue)
      }}
      getOptionLabel={option => `(#${option?.clientNumber}) ${option?.displayName || ''}`.trim()}
      loading={clientLoading}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      disabled={disabled}
      autoHighlight={autoSelectOnTab}
      autoSelect={autoSelectOnTab}
      ListboxProps={{
        role: 'list-box',
        style: {
          maxHeight: '190px'
        },
        onScroll: handleScroll
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={!!errorMessage}
          helperText={errorMessage}
          size={size}
        />
      )}
    />
  )
}

export default ClientDropdown
